import type { InputProps } from '@chakra-ui/react'
import React from 'react'
import type { CustomNumberInputProps } from '../CustomNumberInput'
import { CustomNumberInput } from '../CustomNumberInput'
import { withFormControl, type FormFieldProps } from './hoc/withFormControl'

// Component-specific props
export interface NumberFormControlComponentProps extends Omit<CustomNumberInputProps, 'defaultValue' | 'name' | 'onChange' | 'onBlur' | 'value' | 'isDisabled' | 'ref'> {
  variant?: InputProps['variant']
  autoComplete?: InputProps['autoComplete']
  w?: InputProps['w']
}

// Base component that handles number input logic
export const NumberFormControlBase = React.forwardRef<
  HTMLDivElement,
  NumberFormControlComponentProps & FormFieldProps<number | null, HTMLDivElement>
>((props, ref) => {
  const {
    // Form field props
    value,
    onChange,
    onBlur,
    isDisabled,
    name,
    // Component-specific props
    variant,
    inputProps,
    inputRightElement,
    decimalPlaces,
    onValueChange,
    w,
    autoComplete,
    ...rest
  } = props

  const format = (val: number | null | undefined) => {
    if (val === undefined || val === null) return ''
    return new Intl.NumberFormat('cs-CZ', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    }).format(Number(val))
  }

  return (
    <CustomNumberInput
      ref={ref}
      name={name}
      value={format(value)}
      onChange={(value, valueAsNumber) => {
        if (isNaN(valueAsNumber) && value === '')
          onChange(null)
        else
          onChange(valueAsNumber)
      }}
      onBlur={onBlur}
      isDisabled={isDisabled}
      onValueChange={onValueChange}
      inputProps={{
        ...inputProps,
        autoComplete
      }}
      inputRightElement={inputRightElement}
      variant={variant}
      w={w}
      decimalPlaces={decimalPlaces}
      {...rest}
    />
  )
})

  ; (NumberFormControlBase as any).displayName = 'NumberFormControlBase'

// Export the wrapped component
export const NumberFormControl = withFormControl<NumberFormControlComponentProps, number | null, HTMLDivElement>(NumberFormControlBase)
